import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface TerminateContractDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    contractId: number;
    onSuccess?: () => void;
}

export default function TerminateContractDialog({
    open,
    onOpenChange,
    contractId,
    onSuccess,
}: TerminateContractDialogProps) {
    const queryClient = useQueryClient();
    const [reason, setReason] = useState('');
    // Termination date is required: it decides when the contract stops accruing, so the
    // dialog always sends one. Clicking the selected day in the picker deselects it
    // (react-day-picker single mode) — ignore that instead of silently dropping the date.
    const [terminationDate, setTerminationDate] = useState<Date>(new Date());

    const terminateMutation = useMutation({
        mutationFn: async () => {
            const { data, error } = await supabase.rpc('terminate_contract', {
                contract_id_param: contractId,
                reason_param: reason.trim() || undefined,
                occurred_at_param: terminationDate.toISOString(),
            });
            if (error) throw error;
            return data;
        },
        onSuccess: (result: any) => {
            if (result.success) {
                toast.success('Contract terminated successfully');
                queryClient.invalidateQueries({ queryKey: ['contract-insights', String(contractId)] });
                queryClient.invalidateQueries({ queryKey: ['contracts'] });
                queryClient.invalidateQueries({ queryKey: ['contract-status-for-signing', contractId] });
                setReason('');
                setTerminationDate(new Date());
                onOpenChange(false);
                onSuccess?.();
            } else {
                toast.error(result.error || 'Failed to terminate contract');
            }
        },
        onError: (error) => {
            captureError(error);
            toast.error('Failed to terminate contract');
        },
    });

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        Terminate Contract?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        This will mark the contract as terminated. The contract can be reactivated later if needed.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label htmlFor="termination-date" className="text-sm font-medium">
                            Termination Date
                        </Label>
                        <DatePicker
                            date={terminationDate}
                            setDate={(date) => { if (date) setTerminationDate(date); }}
                            placeholder="Select termination date"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="termination-reason" className="text-sm font-medium">
                            Reason <span className="text-muted-foreground font-normal">(optional)</span>
                        </Label>
                        <Textarea
                            id="termination-reason"
                            placeholder="Why is this contract being terminated?"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={terminateMutation.isPending}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            terminateMutation.mutate();
                        }}
                        disabled={terminateMutation.isPending}
                        className="bg-red-600 hover:bg-red-700 text-white"
                    >
                        {terminateMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : null}
                        Terminate Contract
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
